import { useSignIn } from '@clerk/clerk-react';
import { useState } from 'react';
import { KeyRound, Loader2 } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { DoorlyLogo } from '../../components/DoorlyLogo';

/**
 * Connexion par identifiant + mot de passe, pour les vendeurs des réseaux de
 * boutiques qui n'ont pas d'adresse email.
 *
 * Pourquoi un écran à part plutôt que le `<SignIn />` de Clerk : l'instance
 * présente le code email en premier (`preferred_sign_in_strategy: otp`, un
 * réglage hérité qui n'est plus exposé nulle part dans le dashboard). Le
 * vendeur devait donc traverser un écran de code envoyé à une adresse qui ne
 * reçoit rien, puis trouver « Utiliser une autre méthode ». Deux taps
 * parasites à chaque connexion, sur une tablette partagée entre quatre
 * personnes qui se relogguent en continu.
 *
 * `signIn.create({ identifier, password })` vérifie le mot de passe dans
 * l'appel de création : aucun facteur à sélectionner, aucun écran
 * intermédiaire. Et comme cette route est réservée aux comptes sans email,
 * elle ne change **rien** pour les managers, RP et admins, qui continuent de
 * passer par le formulaire standard et leur code email.
 */
/**
 * Chemin de la connexion vendeur. Exporté pour que l'écran de création de
 * compte puisse donner au responsable la bonne URL à transmettre, sans la
 * réécrire à la main de son côté.
 */
export const VENDOR_SIGN_IN_PATH = '/connexion';

/** Vrai quand l'URL courante est celle de la connexion vendeur. */
export function isVendorSignInPath(): boolean {
  return window.location.pathname.replace(/\/+$/, '') === VENDOR_SIGN_IN_PATH;
}

export function VendorSignIn() {
  const { signIn, setActive, isLoaded } = useSignIn();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signIn || pending) return;
    setError(null);
    setPending(true);
    try {
      const result = await signIn.create({
        identifier: identifier.trim().toLowerCase(),
        password,
      });
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        // Sans cette redirection, l'URL reste `/connexion`. Le routeur ne
        // monte qu'une fois connecté (il vit dans `SignedIn`) et ne connaît
        // pas ce chemin : le vendeur, pourtant authentifié, tombait sur
        // « Page introuvable » juste après avoir réussi sa connexion.
        //
        // `replace` et non `assign` : le retour arrière doit ramener là d'où
        // il venait, pas sur un écran de connexion qu'il vient de passer.
        window.location.replace('/');
        return;
      }
      // Tout autre statut veut dire que Clerk réclame une étape de plus
      // (MFA, vérification d'appareil…). Ce cas ne devrait pas se produire
      // pour un compte vendeur, mais on ne le laisse pas muet : le
      // formulaire standard, lui, sait dérouler n'importe quel parcours.
      setError(
        'Cette connexion demande une étape supplémentaire. Utilisez la connexion standard ci-dessous.',
      );
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="min-h-dvh grid place-items-center p-4">
      <div className="w-full max-w-sm space-y-6">
        <header className="flex flex-col items-center gap-2 text-center">
          <DoorlyLogo variant="full" size={40} />
          <p className="text-ink-mute text-body">Connexion vendeur</p>
        </header>

        <Card className="p-5">
          <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="vendor-identifier" className="text-body font-medium text-ink">
                Identifiant
              </label>
              <input
                id="vendor-identifier"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                // Pas de correction automatique ni de majuscule initiale :
                // sur iOS, un identifiant comme « wee0001289 » se fait
                // volontiers capitaliser ou corriger, et l'utilisateur ne
                // comprend pas pourquoi il est refusé.
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoComplete="username"
                inputMode="text"
                required
                placeholder="wee0001289"
                className="w-full rounded-[var(--radius-sm)] border border-line bg-surface px-3 py-3 text-body font-mono text-ink placeholder:text-ink-mute focus:border-brand focus:outline-none"
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="vendor-password" className="text-body font-medium text-ink">
                Mot de passe
              </label>
              <input
                id="vendor-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                className="w-full rounded-[var(--radius-sm)] border border-line bg-surface px-3 py-3 text-body text-ink focus:border-brand focus:outline-none"
              />
            </div>

            {error && (
              <div
                role="alert"
                className="rounded-[var(--radius-sm)] bg-danger/5 border border-danger/20 text-danger p-3 text-body"
              >
                {error}
              </div>
            )}

            <Button type="submit" disabled={!isLoaded || pending} className="w-full py-3">
              {pending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Connexion…
                </>
              ) : (
                <>
                  <KeyRound className="h-4 w-4" />
                  Se connecter
                </>
              )}
            </Button>
          </form>

          <p className="text-micro text-ink-mute mt-4 leading-snug">
            Mot de passe oublié ? Ce compte n'a pas d'adresse email, donc pas de
            réinitialisation automatique : demandez-en un nouveau à votre responsable.
          </p>
        </Card>

        <p className="text-center text-micro text-ink-mute">
          Vous avez une adresse email ?{' '}
          <a href="/" className="underline hover:text-ink">
            Connexion standard
          </a>
        </p>
      </div>
    </div>
  );
}

/**
 * Traduit une erreur Clerk en message actionnable. On reste volontairement
 * vague sur « identifiant ou mot de passe » : distinguer les deux dirait à un
 * inconnu quels identifiants existent, or ils sont séquentiels par partenaire
 * et donc devinables de proche en proche.
 */
function messageFor(err: unknown): string {
  const code =
    typeof err === 'object' && err !== null && 'errors' in err
      ? (err as { errors?: Array<{ code?: string }> }).errors?.[0]?.code
      : undefined;
  switch (code) {
    case 'form_identifier_not_found':
    case 'form_password_incorrect':
    case 'form_param_format_invalid':
    case 'strategy_for_user_invalid':
      return 'Identifiant ou mot de passe incorrect.';
    case 'user_locked':
      return 'Compte temporairement bloqué après trop de tentatives. Réessayez dans une heure ou contactez votre responsable.';
    case 'session_exists':
      return 'Une session est déjà ouverte sur cet appareil. Rechargez la page.';
    default:
      // Jamais `err.message` : Clerk renvoie de l'anglais brut (« Identifier
      // is invalid. »), qui n'aide pas un vendeur et donne l'impression d'une
      // application cassée. Le détail part en console pour le support.
      console.warn('[connexion vendeur] erreur non mappée:', err);
      return 'Connexion impossible. Vérifiez votre identifiant et votre mot de passe, puis réessayez.';
  }
}
